// Shared Macro Brief card layout math so the card render, the settings panel and
// the resize constraints all agree on how many indicators fit into the section.
// Mirrors `kpiLayout.ts`, but Macro Brief rows are full-width (single column), so
// capacity depends on the section's height only — not its width.

import type { CanvasCard, MacroBriefCardConfig, MacroBriefCardData } from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

// Absolute sanity cap / fallback when the section height is unknown.
export const MACRO_MAX_INDICATORS = 12;

// The drag-handle bar shown on top of every card in edit mode eats into the
// content area, so an indicator that fits in the exported report can still clip
// while editing. Account for it when judging "does it fit".
export const MACRO_EDIT_CHROME_PX = 28;

// Geometry of the Macro Brief card content, mirroring `MacroBriefCard`.
const PADDING_Y = 32; // p: 2 → 16px top + 16px bottom
const HEADER_PX = 30; // "Macro Brief" label (lineHeight) + mb: 1.5
const FOOTER_PX = 39; // "As of …" footer: mt 1.5 + pt 1 + border + text line
const ROW_PX = 48; // one indicator row: label + source lines + 6px×2 padding
const ROW_GAP_PX = 6; // gap between indicator rows
const NARRATIVE_RESERVE_PX = 64; // space kept for the narrative block when shown

/**
 * Number of indicators actually rendered by a Macro Brief card. Prefers the
 * fetched data (what the user sees) and falls back to the configured indicators.
 */
export const countMacroIndicators = (card: CanvasCard): number => {
  const data = card.data as MacroBriefCardData | undefined;
  if (data?.indicators?.length) return data.indicators.length;
  if (card.config.type === "macroBrief") return (card.config as MacroBriefCardConfig).indicators.length;
  return 0;
};

/**
 * Max number of indicators that fit into the card's section for the given pixel
 * height. `chromePx` is height consumed by surrounding UI (e.g. the edit-mode
 * header); `showNarrative` reserves room for the narrative block.
 * Falls back to {@link MACRO_MAX_INDICATORS} when the height is unknown.
 */
export const getMacroBriefCapacity = (heightPx?: number, chromePx = 0, showNarrative = false): number => {
  if (!heightPx) return MACRO_MAX_INDICATORS;

  const narrative = showNarrative ? NARRATIVE_RESERVE_PX : 0;
  const available = heightPx - PADDING_Y - chromePx - HEADER_PX - FOOTER_PX - narrative;
  // n rows occupy n*ROW_PX + (n-1)*ROW_GAP_PX. Not even one row may fit → 0.
  const rows = Math.max(0, Math.floor((available + ROW_GAP_PX) / (ROW_PX + ROW_GAP_PX)));

  return Math.min(rows, MACRO_MAX_INDICATORS);
};

/** Whether `count` indicators fit into a section of the given height. */
export const macroBriefIndicatorsFit = (
  count: number,
  heightPx?: number,
  chromePx = 0,
  showNarrative = false
): boolean => count <= getMacroBriefCapacity(heightPx, chromePx, showNarrative);

/** Smallest grid height (rows) that fits `count` indicators. */
export const getMacroBriefMinGridH = (count: number, chromePx = 0, showNarrative = false): number => {
  for (let h = 1; h <= GRID_CONSTANTS.GRID_COLUMNS; h++) {
    if (count <= getMacroBriefCapacity(h * GRID_CONSTANTS.GRID_CELL_HEIGHT, chromePx, showNarrative)) return h;
  }
  return GRID_CONSTANTS.GRID_COLUMNS;
};
